import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShareAltOutlined,
  EditOutlined,
  CloseOutlined,
  CheckOutlined,
  DollarCircleOutlined,
  DeleteOutlined,
  LockOutlined,
  UnlockOutlined,
  CompassOutlined,
  EuroCircleOutlined,
} from '@ant-design/icons';
import {
  Card,
  Progress,
  Button,
  Collapse,
  List,
  Avatar,
  Space,
  Row,
  Divider,
  message,
  Col,
  Popconfirm,
  Typography,
} from 'antd';
import { useIntl } from 'react-intl';
import { StoreContext } from '../store/index';
import { getDateFromTimestamp, delay, getTimerByDate } from '../utils';
import {
  updateShift,
  removeShift,
  closeShift,
  removeStatsByShift,
  addStat,
  addGame,
  addOwnerGame,
} from './api';
import BookShiftModal from './bookShiftModal';
import Players from '../players/players';
import './index.css';
import { getScore } from './utils';
import RequestModal from '../players/requestModal';
import PaymentModal from './paymentModal';
import ShareModal from './shareModal';
import FinishModal from './finishModal';
import AddPlayersModal from './addPlayersModal';
import AddShiftModal from './addShiftModal';

const Shift = ({
  shift,
  shift: {
    id,
    userId,
    startDate,
    placeName,
    organizerEmail,
    address,
    sport,
    description = '',
    isReserved,
    isClosed,
    isFinished,
    totalPlayers,
    players,
    playRequests,
    bookRequests,
    price,
  },
}) => {
  const store = useContext(StoreContext);
  const {
    user: [user, setUser, setUpdateUser],
  } = store;
  const { Title } = Typography;

  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { Panel } = Collapse;

  const [showBookModal, setShowBookModal] = useState(false);
  const [showAddPlayersModal, setShowAddPlayersModal] = useState(false);
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditShiftModal, setShowEditShiftModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState();
  const [timer, setTimer] = useState(new Date(startDate?.toDate()));
  const [refreshTimer, setRefreshTimer] = useState(false);
  const minuteMs = 60000;
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const actualDate = new Date();
    const dif = new Date(startDate?.toDate()).getTime() - actualDate.getTime();
    const hoursMilli = 1000 * 60 * 60; // milliseconds * seconds * minutes
    setRefreshTimer(false);
    if (Math.abs(dif) < hoursMilli) {
      const intervalId = setInterval(() => {
        setTimer(
          new Date(
            new Date().getTime() - new Date(startDate?.toDate().getTime()),
          ),
        );
      }, 1000);
      return () => clearInterval(intervalId);
    }
    setTimer(null);
  }, [startDate, refreshTimer]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshTimer(true);
    }, minuteMs);
    return () => clearInterval(interval);
  }, []);

  const handleRejectBookRequest = request => {
    updateShift(
      id,
      {
        bookRequests: bookRequests.filter(r => r.userId !== request.userId),
      },
      setLoading,
    );
  };

  const handleConfirmBookRequest = bookRequest => {
    updateShift(
      id,
      {
        players: [
          ...players,
          {
            userId: bookRequest.userId,
            name: bookRequest.userName,
            photoUrl: bookRequest.userPhotoUrl,
          },
        ],
        reserveUserId: bookRequest.userId,
        isReserved: true,
        playRequests: playRequests.filter(r => r.userId !== bookRequest.userId),
        bookRequests: [],
      },
      setLoading,
    );
  };

  const isBookRequestPending = () =>
    bookRequests.find(r => r.userId === user.uid);

  const handleAddPlayers = playersToAdd => {
    if (user.uid === shift.userId) {
      updateShift(
        id,
        {
          players: [...shift.players, ...playersToAdd],
        },
        setLoading,
      );
    } else {
      updateShift(
        id,
        {
          playRequests: [...shift.playRequests, ...playersToAdd],
        },
        setLoading,
      );
    }
  };

  const handleFinishShift = async stats => {
    const updatedStats = stats ?? shift.stats;
    await removeStatsByShift(shift.id, setLoading).then(() => {
      if (!isFinished) {
        updateShift(
          id,
          {
            isFinished: true,
            playRequests: [],
            stats: updatedStats,
          },
          setLoading,
        );
        shift.players.forEach(p => addGame(p.userId));
        addOwnerGame(organizerEmail);
      }

      updatedStats.forEach(
        ({
          userId: uid,
          goals,
          yellowCard,
          redCard,
          absent,
          playerName,
          isLocal,
          gameMinute,
          date,
        }) => {
          if (goals > 0 || yellowCard > 0 || redCard || absent) {
            addStat({
              shiftId: shift.id,
              userId: uid,
              goals,
              yellowCard,
              redCard,
              absent,
              playerName,
              isLocal,
              gameMinute,
              date,
            });
          }
        },
      );
    });

    await delay(2000);
    setUpdateUser(true);
  };

  return (
    <Card
      key={id}
      className="card"
      title={
        <Row align="middle" style={{ marginBottom: '4px' }}>
          <Col xs={16}>
            <Row>
              {isFinished
                ? `${formatMessage({ id: 'shifts.finished' })}`
                : `${
                    isReserved
                      ? formatMessage({ id: 'shifts.reserved' })
                      : formatMessage({ id: 'shifts.not.reserved' })
                  }
               - ${
                 isClosed
                   ? `${formatMessage({ id: 'shifts.closed' })}`
                   : `${formatMessage({ id: 'shifts.not.closed' })}`
               }`}
              {!isFinished &&
                (isClosed ? (
                  <Popconfirm
                    title={formatMessage({ id: 'shifts.actions.confirm.open' })}
                    description={formatMessage({
                      id: 'common.actions.confirm.open.question',
                    })}
                    onConfirm={() => closeShift(shift.id, false, setLoading)}
                    onCancel={() => {}}
                    okText={formatMessage({ id: 'common.yes' })}
                    cancelText={formatMessage({ id: 'common.no' })}
                    disabled={shift.userId !== user.uid}>
                    <UnlockOutlined
                      style={{
                        fontSize: '18px',
                        color: 'gold',
                        marginLeft: '4px',
                      }}
                    />
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title={formatMessage({
                      id: 'shifts.actions.confirm.close',
                    })}
                    description={formatMessage({
                      id: 'common.actions.confirm.close.question',
                    })}
                    onConfirm={() => closeShift(shift.id, true, setLoading)}
                    onCancel={() => {}}
                    okText={formatMessage({ id: 'common.yes' })}
                    cancelText={formatMessage({ id: 'common.no' })}
                    disabled={shift.userId !== user.uid}>
                    <LockOutlined
                      style={{
                        fontSize: '18px',
                        color: 'gold',
                        marginLeft: '4px',
                      }}
                    />
                  </Popconfirm>
                ))}
            </Row>
            <Row>
              <DollarCircleOutlined
                style={{ fontSize: '20px', paddingRight: '4px' }}
              />
              {`${price}€`}
            </Row>
          </Col>
          <Col xs={8}>
            <Row>
              <Button
                style={{ width: '100%', marginTop: '4px' }}
                type="primary"
                loading={loading}
                icon={<ShareAltOutlined />}
                onClick={() => setShowShareModal(true)}>
                {formatMessage({ id: 'common.actions.share' })}
              </Button>
            </Row>
            {shift.userId === user.uid && (
              <Row style={{ marginTop: '4px' }}>
                <Popconfirm
                  title={formatMessage({ id: 'shifts.actions.delete.confirm' })}
                  description={formatMessage({
                    id: 'common.actions.confirm.question',
                  })}
                  onConfirm={() => removeShift(shift.id, setLoading)}
                  onCancel={() => {}}
                  okText={formatMessage({ id: 'common.yes' })}
                  cancelText={formatMessage({ id: 'common.no' })}>
                  <Button
                    style={{ width: '100%' }}
                    danger
                    loading={loading}
                    icon={<DeleteOutlined />}>
                    {formatMessage({ id: 'common.actions.delete' })}
                  </Button>
                </Popconfirm>
              </Row>
            )}
            {(shift.userId === user.uid || user.isAdmin) && !shift.isFinished && (
              <Row>
                <Button
                  style={{ width: '100%', marginTop: '4px' }}
                  type="primary"
                  loading={loading}
                  icon={<EditOutlined />}
                  onClick={() => setShowEditShiftModal(true)}>
                  {formatMessage({ id: 'common.actions.edit' })}
                </Button>
              </Row>
            )}
            {players.find(p => p.userId === user.uid) && user.isAdmin && false && (
              <Row style={{ marginTop: '4px', marginBottom: '4px' }}>
                <Button
                  style={{ width: '100%' }}
                  type="primary"
                  loading={loading}
                  icon={<EuroCircleOutlined style={{ fontSize: '16px' }} />}
                  onClick={() => setShowPaymentModal(true)}>
                  {formatMessage({ id: 'common.actions.pay' })}
                </Button>
              </Row>
            )}
          </Col>
        </Row>
      }>
      <span>
        <Space
          style={{ cursor: 'pointer' }}
          onClick={() =>
            window.open(`https://www.google.com/maps/dir//${address}`)
          }>
          <CompassOutlined style={{ fontSize: '25px' }} />
          {`${placeName} - ${address}`}
        </Space>
      </span>
      <span>{description}</span>
      <Row
        justify="space-evenly"
        style={{
          paddingTop: '16px',
          marginBottom: '8px',
          fontSize: '20px',
        }}>
        <Col>
          <Title level={3}>
            {getScore(shift.stats, shift.localTeam.name, shift.visitTeam.name)}
          </Title>
        </Col>
        {timer && <Col>{getTimerByDate(timer)}</Col>}
      </Row>
      {bookRequests.length > 0 && userId === user.uid && (
        <Collapse
          bordered={false}
          items={
            <Panel
              header={`Solicitudes de reserva: ${bookRequests.length}`}
              key="1">
              <List
                loading={loading}
                dataSource={[...bookRequests]}
                renderItem={bookRequest => (
                  <List.Item
                    key={bookRequest.userId}
                    actions={[
                      <Button
                        type="primary"
                        loading={loading}
                        key={bookRequest.userId}
                        icon={<CloseOutlined />}
                        onClick={() => handleRejectBookRequest(bookRequest)}
                      />,
                      <Button
                        key={bookRequest.userId}
                        icon={<CheckOutlined />}
                        onClick={() => handleConfirmBookRequest(bookRequest)}
                      />,
                    ]}>
                    <List.Item.Meta
                      avatar={<Avatar src={bookRequest.userPhotoUrl} />}
                      title={bookRequest.userName}
                      description={
                        !isReserved
                          ? `${
                              bookRequest.confirmedPlayers
                            }/${totalPlayers} ${formatMessage({
                              id: 'shifts.confirmed',
                            })}`
                          : ''
                      }
                    />
                  </List.Item>
                )}
              />
            </Panel>
          }
        />
      )}
      <Players
        onInvite={() => setShowAddPlayersModal(true)}
        players={players}
        shift={shift}
        onStatChangeInFinishedGame={handleFinishShift}
        timer={timer}
      />
      <Row justify="space-between">
        {user.uid === shift.userId && !isFinished && (
          <Button
            style={{ marginTop: '2px' }}
            className="width100"
            type="primary"
            loading={loading}
            key="primary"
            onClick={() => {
              setShowAddPlayersModal(true);
            }}>
            {formatMessage({ id: 'shifts.actions.add.players' })}
          </Button>
        )}
        {new Date(startDate?.toDate()) <= new Date() &&
          userId === user.uid &&
          !isFinished && (
            <Button
              style={{ marginTop: '2px' }}
              loading={loading}
              className="width100"
              danger
              key="finish"
              type="primary"
              onClick={() => {
                handleFinishShift();
              }}>
              {formatMessage({ id: 'common.actions.finish' })}
            </Button>
          )}
      </Row>
      {!isReserved && !isBookRequestPending() && (
        <Button
          type="primary"
          loading={loading}
          block
          onClick={() => setShowBookModal(true)}>
          Reservar
        </Button>
      )}
      {!isReserved && isBookRequestPending() && (
        <Button loading={loading} disabled block>
          Esperando confirmacion
        </Button>
      )}
      {showBookModal && (
        <BookShiftModal
          shift={shift}
          visible={showBookModal}
          onClose={() => setShowBookModal(false)}
        />
      )}
      {showAddPlayersModal && (
        <AddPlayersModal
          shift={shift}
          onConfirm={handleAddPlayers}
          visible={showAddPlayersModal}
          onClose={() => setShowAddPlayersModal(false)}
        />
      )}
      {showFinishModal && (
        <FinishModal
          shift={shift}
          onConfirm={handleFinishShift}
          visible={showFinishModal}
          onClose={() => setShowFinishModal(false)}
        />
      )}
      {showPaymentModal && (
        <PaymentModal
          shift={shift}
          open={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
        />
      )}
      {showShareModal && (
        <ShareModal
          shift={shift}
          open={showShareModal}
          onClose={() => setShowShareModal(false)}
        />
      )}
      {showEditShiftModal && (
        <AddShiftModal
          shift={shift}
          visible={showEditShiftModal}
          onClose={() => setShowEditShiftModal(false)}
        />
      )}
    </Card>
  );
};

export default Shift;
